import { CafCorpusEntry, CafGuidanceVersion, ClaimFieldOption } from '../../../api/cafCorpus';
import { actionCopy } from '../copy';
import { formatWhen } from '../format';

export type HistoryTitle =
  | 'First label' | 'Changed' | 'Switched off' | 'Switched back on' | 'Label from before changes were tracked';

export interface HistoryItem {
  key: string;
  title: HistoryTitle;
  versionLabel: string;
  when: string;
  who: string;
  reason: string | null;
  rows: Array<{ field: string; value: string }>;
  changes: Array<{ field: string; was: string; now: string }>;
  current: boolean;
  off: boolean;
}

type Reader = (snapshot: CafCorpusEntry, fieldLabel: (field: string) => string) => string;

const dataSummary: Reader = (snapshot, fieldLabel) => {
  const fields = snapshot.extraction_schema?.enabled ? snapshot.extraction_schema.fields : [];
  return fields.length ? fields.map((field) => `${field.label} → ${fieldLabel(field.targetClaimField)}`).join(', ') : 'Off';
};

const FIELDS: Array<[string, Reader]> = [
  ['What happens', (snapshot) => actionCopy(snapshot.correct_action).label],
  ['Why', (snapshot) => snapshot.note || '—'],
  ['Classification', (snapshot) => snapshot.category_override || 'None'],
  ['Copy data into the claim', dataSummary],
  ['Archive emails that quote a reference', (snapshot) => (snapshot.archive_override_refs ? 'On' : 'Off')],
];

/**
 * Oldest-first versions in, newest-first plain-words items out. `complete` must be false
 * whenever a newer page could still exist and hasn't loaded (a later page failed, or the
 * MAX_PAGES cut-off was hit) -- the backend orders pages oldest first, so the last-loaded
 * version is only truly the newest, current one when every page loaded.
 */
export function describeHistory(versions: CafGuidanceVersion[], claimFields: ClaimFieldOption[], complete: boolean): HistoryItem[] {
  const fieldLabel = (field: string) => claimFields.find((option) => option.field === field)?.label ?? 'another claim field';
  return versions.map((version, index): HistoryItem => {
    const snapshot = version.snapshot;
    const previous = index > 0 ? versions[index - 1].snapshot : null;
    const legacy = version.version_provenance === 'content_snapshot';
    const isOn = snapshot.active !== false;
    const wasOn = previous ? previous.active !== false : isOn;
    let title: HistoryTitle = 'Changed';
    if (legacy) title = 'Label from before changes were tracked';
    else if (!previous) title = 'First label';
    else if (wasOn && !isOn) title = 'Switched off';
    else if (!wasOn && isOn) title = 'Switched back on';
    const changes = previous
      ? FIELDS.map(([field, read]) => ({ field, was: read(previous, fieldLabel), now: read(snapshot, fieldLabel) }))
        .filter((change) => change.was !== change.now)
      : [];
    const rows = previous ? [] : FIELDS.slice(0, 2).map(([field, read]) => ({ field, value: read(snapshot, fieldLabel) }));
    const number = typeof version.version === 'number' ? version.version : version.sequence;
    return {
      key: version.id,
      title,
      versionLabel: legacy ? 'Before version 1' : `Version ${number}`,
      when: formatWhen(version.recorded_at),
      // A blank or missing actor never renders as an empty "who" (final wave item 21).
      who: (typeof version.amended_by === 'string' && version.amended_by.trim()) || 'Unknown',
      reason: title === 'First label' ? null : version.reason,
      rows,
      changes,
      current: complete && index === versions.length - 1,
      off: !isOn,
    };
  }).reverse();
}
